import Modal from '../common/Modal';
import { useFirestoreAllArrayContains } from '../../firebase/useFirestore';
import { client } from '../../hooks/Client';
import { db } from '../../firebase/config';
import { useState, useEffect } from 'react';

const SYNC_TYPE_LABELS = {
  MSIEffect:   'Meetstandaard effect',
  Output:      'Output',
  Question:    'Vraag',
  Targetgroup: 'Doelgroep',
  CustomEvent: 'Custom veld',
};

const chunk = (arr, size = 10) =>
  arr.reduce((acc, _, i) => (i % size ? acc : [...acc, arr.slice(i, i + size)]), []);

const fetchNameMap = async (collection, ids) => {
  const map = {};
  if (!ids.length) return map;
  for (const part of chunk([...new Set(ids)].filter(Boolean), 10)) {
    const snap = await db.collection(collection).where('ID', 'in', part).get();
    snap.forEach(d => { const data = d.data(); map[data.ID] = data; });
  }
  return map;
};

const syncLabel = (s, names) => {
  if (s.Type === 'Output')      return names[s.Output]?.Title        || s.Output      || '—';
  if (s.Type === 'MSIEffect')   return names[s.Effect]?.Effect       || s.Effect      || '—';
  if (s.Type === 'Question')    return names[s.Question]?.Question   || s.Question    || '—';
  if (s.Type === 'Targetgroup') return names[s.Targetgroup]?.Organisation || s.Targetgroup || '—';
  if (s.Type === 'CustomEvent') return names[s.CustomEvent]?.Title   || s.CustomEvent || '—';
  return '—';
};

const CopySynchronisationsModal = ({ modalOpen, setModalOpen, onCopy }) => {
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [sourceSyncs, setSourceSyncs] = useState([]);
  const [nameMap, setNameMap] = useState({});
  const [loading, setLoading] = useState(false);
  const [checkedIds, setCheckedIds] = useState(new Set());

  const projects = useFirestoreAllArrayContains('CompagnyMeta', 'Parent', client);

  // Fetch syncs + names when project changes
  useEffect(() => {
    if (!selectedProjectId) {
      setSourceSyncs([]);
      setNameMap({});
      setCheckedIds(new Set());
      return;
    }

    const load = async () => {
      setLoading(true);

      const snap = await db.collection('Synchronisations')
        .where('Parent', '==', client)
        .where('Project', '==', selectedProjectId)
        .get();

      const syncs = snap.docs
        .map(d => d.data())
        .filter(s => s.Status !== 'deleted' && s.Status !== 'deleted-project');

      // Batch-fetch all names in parallel
      const [outputs, effects, questions, targetgroups, customEvents] = await Promise.all([
        fetchNameMap('Outputs',            syncs.filter(s => s.Type === 'Output').map(s => s.Output)),
        fetchNameMap('OutputEffects',      syncs.filter(s => s.Type === 'MSIEffect').map(s => s.Effect)),
        fetchNameMap('QuestionnaireFields', syncs.filter(s => s.Type === 'Question').map(s => s.Question)),
        fetchNameMap('Stakeholders',        syncs.filter(s => s.Type === 'Targetgroup').map(s => s.Targetgroup)),
        fetchNameMap('CustomEventFields',   syncs.filter(s => s.Type === 'CustomEvent').map(s => s.CustomEvent)),
      ]);

      const combined = { ...outputs, ...effects, ...questions, ...targetgroups, ...customEvents };

      setSourceSyncs(syncs);
      setNameMap(combined);
      setCheckedIds(new Set(syncs.map(s => s.ID)));
      setLoading(false);
    };

    load();
  }, [selectedProjectId]);

  const allChecked = sourceSyncs.length > 0 && checkedIds.size === sourceSyncs.length;

  const toggleAll = () => {
    if (allChecked) {
      setCheckedIds(new Set());
    } else {
      setCheckedIds(new Set(sourceSyncs.map(s => s.ID)));
    }
  };

  const toggleOne = (id) => {
    setCheckedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const handleConfirm = () => {
    const selected = sourceSyncs.filter(s => checkedIds.has(s.ID));
    if (!selected.length) return;
    onCopy(selected);
    setSelectedProjectId('');
    setModalOpen(false);
  };

  const handleClose = () => {
    setSelectedProjectId('');
    setModalOpen(false);
  };

  const content = (
    <div>
      <p>Selecteer het project waarvan u de koppelingen wilt kopiëren:</p>
      <select
        value={selectedProjectId}
        onChange={e => setSelectedProjectId(e.target.value)}
      >
        <option value="">-- Selecteer een project --</option>
        {projects && projects.map(project => (
          <option key={project.CompagnyID} value={project.CompagnyID}>
            {project.CommunityName}
          </option>
        ))}
      </select>

      {loading && <p>Laden...</p>}

      {!loading && sourceSyncs.length > 0 && (
        <div>
          <table>
            <thead>
              <tr>
                <th>
                  <input
                    type="checkbox"
                    checked={allChecked}
                    onChange={toggleAll}
                  />
                </th>
                <th>Type</th>
                <th>Naam</th>
              </tr>
            </thead>
            <tbody>
              {sourceSyncs.map(s => (
                <tr key={s.ID}>
                  <td>
                    <input
                      type="checkbox"
                      checked={checkedIds.has(s.ID)}
                      onChange={() => toggleOne(s.ID)}
                    />
                  </td>
                  <td>{SYNC_TYPE_LABELS[s.Type] || s.Type}</td>
                  <td>{syncLabel(s, nameMap)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!loading && selectedProjectId && sourceSyncs.length === 0 && (
        <p>Geen koppelingen gevonden voor dit project.</p>
      )}
    </div>
  );

  return (
    <Modal
      modalOpen={modalOpen}
      setModalOpen={handleClose}
      confirm={handleConfirm}
      pageHeader="Koppelingen kopiëren van ander project"
      content={content}
    />
  );
};

export default CopySynchronisationsModal;
